/**
 * OAuthConsentPage - "Allow claude.ai to use coSow?" (COS-24).
 *
 * The API's GET /oauth/authorize validates the request and sends the browser here
 * with a signed `req`. This page:
 *  1. signs the user in with Firebase ON THIS PAGE (Google popup or password),
 *  2. asks the API what to show (GET /api/oauth/consent?req=),
 *  3. lets the user pick a workspace and Allow / Cancel,
 *  4. sends the browser to the redirect the API returns (the client's callback).
 *
 * Why not /signin: that flow applies the sign-in policy of the workspace the HOST
 * resolves to, not the one the user is about to connect. The consent API enforces
 * the chosen workspace's own policy instead (services/auth/oauth/consent.ts).
 */
import {
  AuthDivider,
  AuthInput,
  AuthMessage,
  AuthPasswordInput,
  AuthSubmitButton,
  Button,
  GoogleAuthButton,
  Select,
  SelectItem,
} from '@protoss/ui';
import type { OAuthConsentInfo, OAuthConsentResult } from '@protoss/shared';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  type User,
} from 'firebase/auth';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthCard, AuthHeader, AuthLayout } from '../../components/auth';
import { usePublicAppConfig } from '../../contexts/PublicAppConfigContext';
import { ApiError, apiFetch } from '../../lib/apiClient';
import { firebaseAuth, googleProvider } from '../../lib/firebase';
import { getPreAuthConfig } from '../../lib/preAuthConfig';
import '../auth/auth-pages.css';
import './oauth-consent.css';

type Phase = 'checking' | 'signin' | 'loading' | 'consent' | 'submitting' | 'error';

export function OAuthConsentPage() {
  const {
    config: { productName },
  } = usePublicAppConfig();
  const [searchParams] = useSearchParams();
  const req = searchParams.get('req') ?? '';

  const [firebaseUser, setFirebaseUser] = React.useState<User | null>(null);
  const [phase, setPhase] = React.useState<Phase>('checking');
  const [info, setInfo] = React.useState<OAuthConsentInfo | null>(null);
  const [workspaceUuid, setWorkspaceUuid] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [isGoogleLoading, setIsGoogleLoading] = React.useState(false);

  const showGoogle = getPreAuthConfig().allowedMethods.includes('google');
  const showPassword = getPreAuthConfig().allowedMethods.includes('password');

  React.useEffect(() => {
    return onAuthStateChanged(firebaseAuth, (u) => {
      setFirebaseUser(u);
      setPhase((p) => (u ? (p === 'checking' || p === 'signin' ? 'loading' : p) : 'signin'));
    });
  }, []);

  React.useEffect(() => {
    if (phase !== 'loading' || !firebaseUser) return;
    if (!req) {
      setError('This sign-in link is incomplete. Start again from the app.');
      setPhase('error');
      return;
    }
    let cancelled = false;
    apiFetch<OAuthConsentInfo>(`/api/oauth/consent?req=${encodeURIComponent(req)}`)
      .then((result) => {
        if (cancelled) return;
        setInfo(result);
        setWorkspaceUuid(result.workspaces[0]?.uuid ?? '');
        setPhase('consent');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError && err.statusCode === 400
            ? 'This sign-in request has expired. Close this tab and start again from the app.'
            : err instanceof ApiError && err.statusCode === 401
              ? 'We could not find a coSow account for this sign-in.'
              : 'Something went wrong. Please try again.'
        );
        setPhase('error');
      });
    return () => {
      cancelled = true;
    };
  }, [phase, firebaseUser, req]);

  const handleGoogle = async () => {
    setError(null);
    setIsGoogleLoading(true);
    try {
      await signInWithPopup(firebaseAuth, googleProvider);
    } catch {
      setError('Unable to sign in with Google. Please try again.');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handlePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await signInWithEmailAndPassword(firebaseAuth, email.trim(), password);
    } catch {
      setError('Incorrect email or password.');
    }
  };

  const decide = async (decision: 'allow' | 'deny') => {
    setPhase('submitting');
    setError(null);
    try {
      const body = decision === 'allow' ? { req, decision, workspaceUuid } : { req, decision };
      const result = await apiFetch<OAuthConsentResult>('/api/oauth/consent', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      window.location.assign(result.redirectTo);
    } catch (err: unknown) {
      setError(
        err instanceof ApiError && err.statusCode === 403
          ? 'Your sign-in method is not allowed for this workspace. Try signing in another way.'
          : err instanceof ApiError && err.statusCode === 400
            ? 'This sign-in request has expired. Close this tab and start again from the app.'
            : 'Something went wrong. Please try again.'
      );
      setPhase('consent');
    }
  };

  const signOutAndRetry = async () => {
    await firebaseAuth.signOut();
    setInfo(null);
    setPhase('signin');
  };

  const clientHost = info?.clientHost ?? 'An app';

  // Clickjacking guard: an Allow button must never be clickable inside another site's
  // frame. The web host sends no frame-ancestors header, so refuse to render here.
  if (window.top !== window.self) {
    return (
      <AuthLayout>
        <AuthCard>
          <AuthHeader title={`Can't connect to ${productName}`} />
          <AuthMessage variant="error">
            This page can't be shown inside another site. Open it in its own tab.
          </AuthMessage>
        </AuthCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthCard>
        {(phase === 'checking' || phase === 'loading') && (
          <AuthHeader title={`Connect to ${productName}`} subtitle="Loading..." />
        )}

        {phase === 'signin' && (
          <>
            <AuthHeader
              title={`Sign in to ${productName}`}
              subtitle="An app wants to connect to your workspace"
            />
            <div className="auth-form-container">
              {error && <AuthMessage variant="error">{error}</AuthMessage>}
              {showGoogle && (
                <>
                  <GoogleAuthButton onClick={handleGoogle} isLoading={isGoogleLoading}>
                    Continue with Google
                  </GoogleAuthButton>
                  {showPassword && <AuthDivider />}
                </>
              )}
              {showPassword && (
                <form onSubmit={handlePassword} className="auth-form" noValidate>
                  <AuthInput
                    type="email"
                    name="email"
                    label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                  <AuthPasswordInput
                    name="password"
                    label="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                  <AuthSubmitButton isLoading={false}>Sign In</AuthSubmitButton>
                </form>
              )}
            </div>
          </>
        )}

        {(phase === 'consent' || phase === 'submitting') && info && (
          <>
            <AuthHeader
              title={`Allow ${clientHost} to use ${productName}?`}
              subtitle={`Signed in as ${firebaseUser?.email ?? 'you'}`}
            />
            <div className="auth-form-container">
              {error && <AuthMessage variant="error">{error}</AuthMessage>}

              {info.workspaces.length === 0 ? (
                <AuthMessage variant="warning">
                  You don't have a {productName} workspace you can connect with this sign-in. Ask
                  your workspace admin for access.
                </AuthMessage>
              ) : (
                <>
                  <p className="oauth-consent__text">
                    <strong className="oauth-consent__strong">{clientHost}</strong> will be able to
                    do everything your role allows in the workspace you choose: read and update
                    deals, estimates and SOWs. You can disconnect it at any time.
                  </p>

                  {info.workspaces.length > 1 ? (
                    <div className="oauth-consent__field">
                      <span className="oauth-consent__label">Workspace</span>
                      <Select
                        value={workspaceUuid}
                        onValueChange={setWorkspaceUuid}
                        size="comfortable"
                      >
                        {info.workspaces.map((w) => (
                          <SelectItem key={w.uuid} value={w.uuid}>
                            {w.name}
                          </SelectItem>
                        ))}
                      </Select>
                    </div>
                  ) : (
                    <p className="oauth-consent__text">
                      Workspace:{' '}
                      <strong className="oauth-consent__strong">{info.workspaces[0]!.name}</strong>
                    </p>
                  )}

                  {info.loopback && (
                    <AuthMessage variant="warning">
                      This sends you back to a program on this computer ({info.redirectHost}). Only
                      continue if you just started this from Claude Code or another tool you trust.
                    </AuthMessage>
                  )}

                  <p className="oauth-consent__redirect">
                    After you choose, you'll be sent to {info.redirectHost}.
                  </p>
                </>
              )}

              <div className="oauth-consent__actions">
                <Button
                  variant="outline"
                  onClick={() => void decide('deny')}
                  disabled={phase === 'submitting'}
                >
                  Cancel
                </Button>
                {info.workspaces.length > 0 && (
                  <Button
                    variant="primary"
                    onClick={() => void decide('allow')}
                    disabled={phase === 'submitting' || !workspaceUuid}
                  >
                    Allow
                  </Button>
                )}
              </div>

              <button
                type="button"
                className="oauth-consent__switch"
                onClick={() => void signOutAndRetry()}
              >
                Not you? Sign in with another account
              </button>
            </div>
          </>
        )}

        {phase === 'error' && (
          <>
            <AuthHeader title={`Can't connect to ${productName}`} />
            <div className="auth-form-container">
              <AuthMessage variant="error">{error}</AuthMessage>
              {firebaseUser && (
                <button
                  type="button"
                  className="oauth-consent__switch"
                  onClick={() => void signOutAndRetry()}
                >
                  Sign in with another account
                </button>
              )}
            </div>
          </>
        )}
      </AuthCard>
    </AuthLayout>
  );
}
